import {Component, Inject, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from '@angular/material/dialog';
import { ModalAddAttachmentComponent } from '../modal-add-attachment/modal-add-attachment.component';
import { SurveyService } from '../../services/surveys.service';

@Component({
    selector: 'app-modal-show-evidences',
    templateUrl: './modal-show-evidences.component.html',
    styleUrls: ['./modal-show-evidences.component.scss'],
    standalone: false
})
export class ModalShowEvidencesComponent implements OnInit {
  userFullName: any;
  displayedColumns = ['name', 'type', 'loaded_at', 'actions'];
  attachments = [];
  editable = false;

  constructor(
    public dialogRef: MatDialogRef<ModalShowEvidencesComponent>,
    @Inject(MAT_DIALOG_DATA) public data: {
      userFullName?: string,
      attachments: any[],
      editable?: boolean,
      surveyId?: number,
      questionId?: number,
      getSurveyId?: () => number,
      getQuestionId?: () => number
    },
    private dialog: MatDialog,
    private surveyService: SurveyService
  ) {
    this.userFullName = data.userFullName;
    this.attachments = data.attachments;
    this.editable = data.editable || false;
  }

  get surveyId(): number | undefined {
    return this.data.getSurveyId ? this.data.getSurveyId() : this.data.surveyId;
  }

  get questionId(): number | undefined {
    return this.data.getQuestionId ? this.data.getQuestionId() : this.data.questionId;
  }

  ngOnInit(): void {
    if (this.editable) {
      this.displayedColumns = ['name', 'type', 'loaded_at', 'actions'];
    } else {
      this.displayedColumns = ['name', 'type', 'loaded_at', 'actions'];
    }
    // Note: 'actions' will contain both View and Delete (if editable)
  }

  public closeDialogue(event: any = null) {
    this.dialogRef.close(event);
  }

  public updateAttachments(newAttachments: any[]) {
    this.attachments = newAttachments;
    // No need for [...this.attachments] if we replace it,
    // but just in case for mat-table reactivity:
    this.attachments = [...this.attachments];
  }

  deleteAttachment(index: number) {
    if (index >= 0 && index < this.attachments.length) {
      const attachment = this.attachments[index];

      // If we have an ID and surveyId, it's a persisted attachment on a non-draft survey
      if (attachment.id && this.surveyId) {
        if (this.questionId) {
          this.surveyService.deleteQuestionAttachment(this.surveyId, this.questionId, attachment.id, () => {
             this.finalizeDeletion(index);
          });
        } else {
          this.surveyService.deleteSurveyAttachment(this.surveyId, attachment.id, () => {
             this.finalizeDeletion(index);
          });
        }
      } else {
        this.finalizeDeletion(index);
      }
    }
  }

  private finalizeDeletion(index: number) {
    this.attachments.splice(index, 1);
    this.attachments = [...this.attachments]; // Trigger table refresh
  }

  public onClickAddNewEvidence(attachment_type: string) {
    const dialogRef = this.dialog.open(ModalAddAttachmentComponent, {
      panelClass: 'large-dialog',
      data: {
        title: 'modals.modal-add-attachment.add-evidences',
        attachmentType: attachment_type,
        uploadUrl: () => {
          let url = 'upload/tmp-expert-proposal-file';
          if (this.surveyId && attachment_type === 'FILE') {
            if (this.questionId) {
              url = `surveys/${this.surveyId}/questions/${this.questionId}/attachments`;
            } else {
              url = `surveys/${this.surveyId}/attachments`;
            }
          }
          return url;
        },
      },
    });

    dialogRef.afterClosed().subscribe((attachmentData) => {
      if (attachmentData) {
        let newAttachments = [];
        if (["PUBMED", "FILE", "LINK"].includes(attachmentData.attachmentType)) {
          if (attachmentData.attachmentType === "PUBMED") {
            const obj = {
              type: 'PUBMED',
              PMID: attachmentData.attachmentObject.PMID,
              title: attachmentData.attachmentObject.ArticleTitle || attachmentData.attachmentObject.title,
              link: attachmentData.attachmentObject.link,
              created_at: new Date().toISOString(),
              isNew: true,
            };

            // Pubmed always needs a second call to persist if not in draft
            if (this.surveyId) {
              const body = { PMID: obj.PMID, title: obj.title, link: obj.link };
              if (this.questionId) {
                this.surveyService.addQuestionAttachment(this.surveyId, this.questionId, 'PUBMED', body, (res) => {
                  this.attachments.push(res);
                  this.attachments = [...this.attachments];
                });
              } else {
                this.surveyService.addSurveyAttachment(this.surveyId, 'PUBMED', body, (res) => {
                  this.attachments.push(res);
                  this.attachments = [...this.attachments];
                });
              }
            } else {
              this.attachments.push(obj);
              this.attachments = [...this.attachments];
            }
          } else if (attachmentData.attachmentType === "LINK") {
            const obj = {
              ...attachmentData.attachmentObject,
              type: 'LINK',
              created_at: new Date().toISOString(),
              isNew: true,
            };

            // Link needs a second call to persist if not in draft
            if (this.surveyId) {
              const body = { title: obj.title, link: obj.link };
              if (this.questionId) {
                this.surveyService.addQuestionAttachment(this.surveyId, this.questionId, 'LINK', body, (res) => {
                  this.attachments.push(res);
                  this.attachments = [...this.attachments];
                });
              } else {
                this.surveyService.addSurveyAttachment(this.surveyId, 'LINK', body, (res) => {
                  this.attachments.push(res);
                  this.attachments = [...this.attachments];
                });
              }
            } else {
              this.attachments.push(obj);
              this.attachments = [...this.attachments];
            }
          } else {
            // Processing FILE
            if (this.surveyId) {
              // Direct upload already returned the saved objects (or array of objects)
              // Handle both single and array formats
              const savedObjects = Array.isArray(attachmentData.attachmentObject)
                ? attachmentData.attachmentObject
                : [attachmentData.attachmentObject];

              this.attachments.push(...savedObjects);
              this.attachments = [...this.attachments];
            } else {
              // Draft mode - uses tmp files
              attachmentData.attachmentObject?.forEach((file) => {
                const obj = {
                  ...file,
                  type: 'FILE',
                  created_at: new Date().toISOString(),
                  isNew: true,
                };
                this.attachments.push(obj);
              });
              this.attachments = [...this.attachments];
            }
          }
        }
      }
    });
  }
}
